import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from "recharts";
import { 
  ArrowLeft, Plus, Zap, Flame, Edit, Trash2, Save, Users, FileText, Euro, KeyRound, X, Copy, Info,
  TrendingUp, TrendingDown, CheckCircle, XCircle, Clock, MapPin, Gauge, Search, Shield, ShieldOff, UserPlus, FileCheck, Receipt, Building2, AlertTriangle, HelpCircle, UploadCloud, Phone, Briefcase,
  QrCode, FileDown, UserCog, BarChart3, Scan, Star, Link2, Eye, Download, Share2, Printer, Calculator
} from "lucide-react";
import SmartOcrPanel from "../components/SmartOcrPanel";
import ComparisonEngine from "../components/ComparisonEngine";
import AdvancedDashboard from "../components/AdvancedDashboard";
import CompanyVerification from "../components/CompanyVerification";
import BillSimulator from "../components/BillSimulator";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const STATUS_COLORS = ['#22c55e', '#ef4444']; // Green, Red

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyOffer = { 
  tipo: "luce", nome: "", descrizione: "", prezzo_kwh: "", prezzo_smc: "", costo_fisso_mensile: "", vantaggi: [""], codice_offerta: "",
  // Classification fields
  macro_categoria: "domestico", sotto_categoria: "residente", tipologia_offerta: "fisso", indice: "PUN", opzione_bioraria: false,
  codice_arera_135: "", data_inizio_vendibilita: "", data_fine_vendibilita: "", durata_mesi: "12",
  prezzo_f1: "", prezzo_f2: "", prezzo_f3: "", vendita_senza_spread: false,
  // Gas-specific pricing for dual
  prezzo_smc_dual: "", costo_fisso_mensile_gas: "", indice_gas: "PSV",
  // Dual fuel combined discount
  sconto_dual_combinato: "", sconto_dual_combinato_tipo: "euro_mese",
  // Discount fields with values
  sconto_sepa: false, sconto_sepa_valore: "", sconto_sepa_tipo: "euro_mese",
  sconto_online: false, sconto_online_valore: "", sconto_online_tipo: "euro_mese",
  sconto_auto_bolletta: false, sconto_auto_valore: "", sconto_auto_tipo: "euro_mese",
  bonus_benvenuto: "", bonus_benvenuto_tipo: "euro",
  sconto_dual: false, sconto_dual_valore: "", sconto_dual_tipo: "euro_mese",
  note_sconti: "",
  // Technical fields
  potenza_min: "3", potenza_max: "6", tipologia_dispacciamento: "base", deposito_cauzionale: false,
  visibile_online: true, vendibile_online: true, solo_admin: false, off_placet: false, off_vulnerabili: false,
  link_cte: "", descrizione_cte: ""
};

const emptyPrecheck = { codice: "", tipo: "pod", nome_cliente: "", codice_fiscale: "", indirizzo: "" };
const emptyPlafond = { cliente_id: "", numero_protocollo: "", data_inizio: "", data_fine: "", importo_plafond: "", importo_utilizzato: "0", note: "" };

export default function AdminPanel() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState(emptyOffer);
  const [saving, setSaving] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const [marketPrices, setMarketPrices] = useState(null);
  const [referenceData, setReferenceData] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Pre-check state
  const [precheckForm, setPrecheckForm] = useState(emptyPrecheck);
  const [precheckHistory, setPrecheckHistory] = useState([]);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  
  // Plafond IVA state
  const [plafondDialogOpen, setPlafondDialogOpen] = useState(false);
  const [plafondForm, setPlafondForm] = useState(emptyPlafond);
  const [plafonds, setPlafonds] = useState([]);
  const [editingPlafond, setEditingPlafond] = useState(null);
  
  // Switch Requests state
  const [switchRequests, setSwitchRequests] = useState([]);
  const [switchLoading, setSwitchLoading] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", is_admin: false });
  const [resetPwUser, setResetPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  // Activation Requests state
  const [activationRequests, setActivationRequests] = useState([]);
  const [activationLoading, setActivationLoading] = useState(false);
  const [selectedActivation, setSelectedActivation] = useState(null);

  // ===== NUOVE FUNZIONALITÀ SMART =====
  
  // Smart OCR / Comparazione state
  const [smartOcrResult, setSmartOcrResult] = useState(null);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [selectedOffersForComparison, setSelectedOffersForComparison] = useState([]);
  
  // Venditori state
  const [venditori, setVenditori] = useState([]);
  const [venditoreDialogOpen, setVenditoreDialogOpen] = useState(false);
  const [editingVenditore, setEditingVenditore] = useState(null);
  const [venditoreForm, setVenditoreForm] = useState({
    nome: "", cognome: "", email: "", telefono: "", codice_agente: "",
    offerte_abilitate: [], provvigione_percentuale: "2", stato: "attivo"
  });
  
  // Ranking Offerte state
  const [offerRankings, setOfferRankings] = useState({});
  
  // Tracciabilità state
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  const [codiceBuilder, setCodiceBuilder] = useState({
    codice_arera: "8ENRGY", commodity: "", tipologia_offerta: "S", tipologia_prezzo: "F", tipologia_tariffa: "M", tipo_placet: "L", info_aggiuntive: ""
  });

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (formData.tipo === "luce") setCodiceBuilder(prev => ({ ...prev, commodity: "E" }));
    else if (formData.tipo === "gas") setCodiceBuilder(prev => ({ ...prev, commodity: "G" }));
  }, [formData.tipo]);

  useEffect(() => {
    const { codice_arera, commodity, tipologia_offerta, tipologia_prezzo, tipologia_tariffa, tipo_placet, info_aggiuntive } = codiceBuilder;
    if (codice_arera && commodity) {
      const paddedCodice = codice_arera.toUpperCase().padEnd(6, '0').slice(0, 6);
      const prefix = `${paddedCodice}${commodity}${tipologia_offerta}${tipologia_prezzo}${tipologia_tariffa}${tipo_placet}`;
      const suffix = info_aggiuntive.toUpperCase().replace(/[^A-Z0-9]/g, '').padEnd(21, '0').slice(0, 21);
      setFormData(prev => ({ ...prev, codice_offerta: prefix + suffix }));
    }
  }, [codiceBuilder]);

  const fetchData = async () => {
    try {
      const [offersRes, statsRes, pricesRes, refRes, precheckRes, plafondRes, switchRes, usersRes, activationRes] = await Promise.all([
        fetch(`${API}/admin/offers`, { credentials: "include" }),
        fetch(`${API}/stats/admin`, { credentials: "include" }),
        fetch(`${API}/admin/market-prices`, { credentials: "include" }),
        fetch(`${API}/reference/all`, { credentials: "include" }),
        fetch(`${API}/precheck/history`, { credentials: "include" }),
        fetch(`${API}/admin/plafond-iva`, { credentials: "include" }),
        fetch(`${API}/admin/switch/requests`, { credentials: "include" }),
        fetch(`${API}/admin/users`, { credentials: "include" }),
        fetch(`${API}/admin/activation-requests`, { credentials: "include" })
      ]);
      
      if (offersRes.ok) setOffers(await offersRes.json() || []);
      if (statsRes.ok) setAdminStats(await statsRes.json());
      if (pricesRes.ok) setMarketPrices(await pricesRes.json());
      if (refRes.ok) setReferenceData(await refRes.json());
      if (precheckRes.ok) setPrecheckHistory(await precheckRes.json() || []);
      if (plafondRes.ok) setPlafonds(await plafondRes.json() || []);
      if (switchRes.ok) setSwitchRequests(await switchRes.json() || []);
      if (usersRes.ok) setUsers(await usersRes.json() || []);
      if (activationRes.ok) setActivationRequests(await activationRes.json() || []);
      
      // Fetch venditori
      try {
        const venditoriRes = await fetch(`${API}/admin/venditori`, { credentials: "include" });
        if (venditoriRes.ok) setVenditori(await venditoriRes.json() || []);
      } catch (e) { /* venditori fetch optional */ }
      
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Offer handlers
  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      setFormData({ 
        ...emptyOffer,
        ...offer,
        // Ensure strings for inputs
        prezzo_kwh: offer.prezzo_kwh?.toString() || "",
        prezzo_smc: offer.prezzo_smc?.toString() || "",
        costo_fisso_mensile: offer.costo_fisso_mensile?.toString() || "",
        prezzo_f1: offer.prezzo_f1?.toString() || "",
        prezzo_f2: offer.prezzo_f2?.toString() || "",
        prezzo_f3: offer.prezzo_f3?.toString() || "",
        potenza_min: offer.potenza_min?.toString() || "3",
        potenza_max: offer.potenza_max?.toString() || "6",
        durata_mesi: offer.durata_mesi?.toString() || "12",
        vantaggi: offer.vantaggi?.length > 0 ? offer.vantaggi : [""]
      });
      
      if (offer.codice_offerta?.length >= 11) {
        setCodiceBuilder({ codice_arera: offer.codice_offerta.slice(0, 6), commodity: offer.codice_offerta[6], tipologia_offerta: offer.codice_offerta[7], tipologia_prezzo: offer.codice_offerta[8], tipologia_tariffa: offer.codice_offerta[9], tipo_placet: offer.codice_offerta[10], info_aggiuntive: offer.codice_offerta.slice(11).replace(/0+$/, '') });
      }
    } else {
      setEditingOffer(null);
      setFormData(emptyOffer);
      setCodiceBuilder({ codice_arera: "8ENRGY", commodity: "E", tipologia_offerta: "S", tipologia_prezzo: "F", tipologia_tariffa: "M", tipo_placet: "L", info_aggiuntive: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Check if codice_offerta is the default (all zeros) - if so, set to null
      let finalCodiceOfferta = formData.codice_offerta;
      if (finalCodiceOfferta && finalCodiceOfferta.includes('000000000000000000000')) {
        finalCodiceOfferta = null;
      }
      
      // Prepare payload with types
      const isDual = formData.tipo === "dual";
      const isLuce = formData.tipo === "luce" || isDual;
      const isGas = formData.tipo === "gas" || isDual;
      
      const payload = { 
        ...formData,
        prezzo_kwh: isLuce && formData.prezzo_kwh ? parseFloat(formData.prezzo_kwh) : null, 
        prezzo_smc: isGas && (isDual ? formData.prezzo_smc_dual : formData.prezzo_smc) ? parseFloat(isDual ? formData.prezzo_smc_dual : formData.prezzo_smc) : null, 
        costo_fisso_mensile: formData.costo_fisso_mensile ? parseFloat(formData.costo_fisso_mensile) : 0,
        prezzo_f1: isLuce && formData.prezzo_f1 ? parseFloat(formData.prezzo_f1) : null,
        prezzo_f2: isLuce && formData.prezzo_f2 ? parseFloat(formData.prezzo_f2) : null,
        prezzo_f3: isLuce && formData.prezzo_f3 ? parseFloat(formData.prezzo_f3) : null,
        potenza_min: isLuce && formData.potenza_min ? parseFloat(formData.potenza_min) : null,
        potenza_max: isLuce && formData.potenza_max ? parseFloat(formData.potenza_max) : null,
        durata_mesi: formData.durata_mesi ? parseInt(formData.durata_mesi) : 12,
        vantaggi: formData.vantaggi ? formData.vantaggi.filter(v => v && v.trim() !== "") : [], 
        codice_offerta: finalCodiceOfferta,
        // Dual Fuel fields
        prezzo_smc_dual: isDual && formData.prezzo_smc_dual ? parseFloat(formData.prezzo_smc_dual) : null,
        costo_fisso_mensile_gas: isDual && formData.costo_fisso_mensile_gas ? parseFloat(formData.costo_fisso_mensile_gas) : null,
        indice_gas: isDual ? (formData.indice_gas || "PSV") : null,
        sconto_dual_combinato: isDual && formData.sconto_dual_combinato ? parseFloat(formData.sconto_dual_combinato) : null,
        sconto_dual_combinato_tipo: isDual ? formData.sconto_dual_combinato_tipo : "euro_mese",
        // Discount fields
        sconto_sepa_valore: formData.sconto_sepa_valore ? parseFloat(formData.sconto_sepa_valore) : null,
        sconto_online_valore: formData.sconto_online_valore ? parseFloat(formData.sconto_online_valore) : null,
        sconto_auto_valore: formData.sconto_auto_valore ? parseFloat(formData.sconto_auto_valore) : null,
        sconto_dual_valore: formData.sconto_dual_valore ? parseFloat(formData.sconto_dual_valore) : null,
        bonus_benvenuto: formData.bonus_benvenuto ? parseFloat(formData.bonus_benvenuto) : null
      };

      const url = editingOffer ? `${API}/admin/offers/${editingOffer.offer_id}` : `${API}/admin/offers`;
      const response = await fetch(url, { method: editingOffer ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
      
      let data;
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        console.error("Response text:", text);
        throw new Error("Errore di comunicazione con il server");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Errore nel salvataggio");
      }
      
      toast.success(editingOffer ? "Offerta aggiornata!" : "Offerta creata!");
      setDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!confirm("Eliminare questa offerta?")) return;
    await fetch(`${API}/admin/offers/${offerId}`, { method: "DELETE", credentials: "include" });
    toast.success("Offerta eliminata!");
    fetchData();
  };

  // Pre-check handlers
  const handlePrecheck = async (e) => {
    e.preventDefault();
    setPrecheckLoading(true);
    try {
      const response = await fetch(`${API}/precheck/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(precheckForm)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail);
      toast.success(result.esito === "verificato" ? "Verifica completata con successo!" : "Verifica completata con errori");
      setPrecheckForm(emptyPrecheck);
      fetchData();
    } catch (error) { toast.error(error.message); } finally { setPrecheckLoading(false); }
  };

  // Plafond IVA handlers
  const handleOpenPlafondDialog = (plafond = null) => {
    if (plafond) {
      setEditingPlafond(plafond);
      setPlafondForm({ cliente_id: plafond.cliente_id, numero_protocollo: plafond.numero_protocollo, data_inizio: plafond.data_inizio, data_fine: plafond.data_fine, importo_plafond: plafond.importo_plafond.toString(), importo_utilizzato: plafond.importo_utilizzato.toString(), note: plafond.note || "" });
    } else {
      setEditingPlafond(null);
      setPlafondForm(emptyPlafond);
    }
    setPlafondDialogOpen(true);
  };

  const handlePlafondSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...plafondForm, importo_plafond: parseFloat(plafondForm.importo_plafond), importo_utilizzato: parseFloat(plafondForm.importo_utilizzato || 0) };
      const url = editingPlafond ? `${API}/admin/plafond-iva/${editingPlafond.plafond_id}` : `${API}/admin/plafond-iva`;
      const response = await fetch(url, { method: editingPlafond ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
      if (!response.ok) { const data = await response.json(); throw new Error(data.detail); }
      toast.success(editingPlafond ? "Plafond aggiornato!" : "Plafond creato!");
      setPlafondDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const handleDeletePlafond = async (plafondId) => {
    if (!confirm("Eliminare questo Plafond IVA?")) return;
    await fetch(`${API}/admin/plafond-iva/${plafondId}`, { method: "DELETE", credentials: "include" });
    toast.success("Plafond eliminato!");
    fetchData();
  };

  // User handlers
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/users?is_admin=${userForm.is_admin}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          name: userForm.name,
          email: userForm.email,
          password: userForm.password
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Errore");
      }
      toast.success("Utente creato con successo!");
      setUserDialogOpen(false);
      setUserForm({ name: "", email: "", password: "", is_admin: false });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (userId, isAdmin) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?is_admin=${isAdmin}`, {
        method: "PUT",
        credentials: "include"
      });
      if (!response.ok) throw new Error("Errore aggiornamento ruolo");
      toast.success("Ruolo aggiornato!");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Errore");
      }
      toast.success("Utente eliminato");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwUser || !newPassword || newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    try {
      const response = await fetch(`${API}/admin/users/${resetPwUser.user_id}/password`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ password: newPassword })
      });
      if (!response.ok) { const d = await response.json(); throw new Error(d.detail || "Errore"); }
      toast.success(`Password aggiornata per ${resetPwUser.name || resetPwUser.email}`);
      setResetPwUser(null);
      setNewPassword("");
    } catch (error) { toast.error(error.message); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato!"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard"><img src={LOGO_URL} alt="8energy" className="h-8" /></Link>
              <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">Admin</span>
            </div>
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button></Link>
          </div>
        </div>
      </nav>

      <main className="max-w-[95vw] mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1 w-full max-w-6xl mb-8">
            <TabsTrigger value="dashboard" className="text-xs">Dashboard</TabsTrigger>
            <TabsTrigger value="smart-ocr" className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white data-[state=active]:from-purple-600 data-[state=active]:to-pink-600">🔮 Smart OCR</TabsTrigger>
            <TabsTrigger value="comparazione" className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white data-[state=active]:from-green-600 data-[state=active]:to-emerald-600">📊 Comparazione</TabsTrigger>
            <TabsTrigger value="venditori" className="text-xs bg-gradient-to-r from-blue-500 to-cyan-500 text-white data-[state=active]:from-blue-600 data-[state=active]:to-cyan-600">👥 Venditori</TabsTrigger>
            <TabsTrigger value="simulatore" className="text-xs">Simulatore</TabsTrigger>
            <TabsTrigger value="activation" className="text-xs">Attivazioni</TabsTrigger>
            <TabsTrigger value="switch" className="text-xs">Switch</TabsTrigger>
            <TabsTrigger value="offerte" className="text-xs">Offerte</TabsTrigger>
            <TabsTrigger value="users" className="text-xs">Utenti</TabsTrigger>
            <TabsTrigger value="precheck" className="text-xs">Pre-check</TabsTrigger>
            <TabsTrigger value="plafond" className="text-xs">Plafond</TabsTrigger>
            <TabsTrigger value="azienda" className="text-xs">Verifica P.IVA</TabsTrigger>
            <TabsTrigger value="riferimento" className="text-xs">Riferimento</TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard">
            <AdvancedDashboard stats={adminStats} marketPrices={marketPrices} precheckHistory={precheckHistory} onTabChange={setActiveTab} />
          </TabsContent>

          {/* ===== SMART OCR TAB ===== */}
          <TabsContent value="smart-ocr">
            <SmartOcrPanel onResultReady={(data) => {
              // Map new format to legacy format for comparazione tab compatibility
              const f = data.fields || {};
              const ident = f.dati_identificativi || {};
              const consumo = f.dati_consumo || {};
              const econ = f.dati_economici || {};
              setSmartOcrResult({
                tipo: ident.tipo_fornitura?.value || "luce",
                fornitore: ident.fornitore?.value || "N/A",
                pod_pdr: ident.pod_pdr?.value || "N/A",
                consumo: consumo.consumo_periodo?.value || consumo.consumo_annuo_stimato?.value || 0,
                prezzo_energia: econ.prezzo_energia_kwh?.value || 0,
                totale: econ.totale_bolletta?.value || 0,
                periodo: consumo.periodo_riferimento?.value || "",
                cliente: ident.intestatario?.value || "",
                indirizzo: (f.dati_tecnici || {}).indirizzo_fornitura?.value || "",
                confidence: data.overall_confidence || 0,
                _full: data
              });
              setActiveTab('comparazione');
            }} />
          </TabsContent>

          {/* ===== COMPARAZIONE TAB ===== */}
          <TabsContent value="comparazione">
            <ComparisonEngine billData={smartOcrResult} offers={offers} />
          </TabsContent>

          {/* ===== VENDITORI TAB ===== */}
          <TabsContent value="venditori">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                    <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-2 rounded-xl">
                      <UserCog className="w-6 h-6" />
                    </span>
                    Gestione Venditori
                  </h2>
                  <p className="text-muted-foreground mt-1">Profili venditori/agenti con offerte abilitate</p>
                </div>
                <Dialog open={venditoreDialogOpen} onOpenChange={setVenditoreDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                      <Plus className="w-4 h-4 mr-2" /> Nuovo Venditore
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingVenditore ? 'Modifica Venditore' : 'Nuovo Venditore'}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div>
                        <Label>Nome</Label>
                        <Input value={venditoreForm.nome} onChange={e => setVenditoreForm({...venditoreForm, nome: e.target.value})} />
                      </div>
                      <div>
                        <Label>Cognome</Label>
                        <Input value={venditoreForm.cognome} onChange={e => setVenditoreForm({...venditoreForm, cognome: e.target.value})} />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={venditoreForm.email} onChange={e => setVenditoreForm({...venditoreForm, email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Telefono</Label>
                        <Input value={venditoreForm.telefono} onChange={e => setVenditoreForm({...venditoreForm, telefono: e.target.value})} />
                      </div>
                      <div>
                        <Label>Codice Agente</Label>
                        <Input value={venditoreForm.codice_agente} onChange={e => setVenditoreForm({...venditoreForm, codice_agente: e.target.value})} placeholder="AG001" />
                      </div>
                      <div>
                        <Label>Provvigione %</Label>
                        <Input type="number" value={venditoreForm.provvigione_percentuale} onChange={e => setVenditoreForm({...venditoreForm, provvigione_percentuale: e.target.value})} />
                      </div>
                      <div className="col-span-2">
                        <Label>Offerte Abilitate</Label>
                        <div className="grid grid-cols-3 gap-2 mt-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                          {offers.map(offer => (
                            <label key={offer.offer_id} className="flex items-center gap-2 text-sm">
                              <Checkbox 
                                checked={venditoreForm.offerte_abilitate.includes(offer.offer_id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setVenditoreForm({...venditoreForm, offerte_abilitate: [...venditoreForm.offerte_abilitate, offer.offer_id]});
                                  } else {
                                    setVenditoreForm({...venditoreForm, offerte_abilitate: venditoreForm.offerte_abilitate.filter(id => id !== offer.offer_id)});
                                  }
                                }}
                              />
                              {offer.nome}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Stato</Label>
                        <Select value={venditoreForm.stato} onValueChange={v => setVenditoreForm({...venditoreForm, stato: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="attivo">Attivo</SelectItem>
                            <SelectItem value="sospeso">Sospeso</SelectItem>
                            <SelectItem value="inattivo">Inattivo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setVenditoreDialogOpen(false)}>Annulla</Button>
                      <Button onClick={async () => {
                        try {
                          const response = await fetch(`${API}/admin/venditori`, {
                            method: editingVenditore ? 'PUT' : 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify(editingVenditore ? {...venditoreForm, venditore_id: editingVenditore.venditore_id} : venditoreForm)
                          });
                          if (response.ok) {
                            toast.success(editingVenditore ? 'Venditore aggiornato' : 'Venditore creato');
                            setVenditoreDialogOpen(false);
                            setVenditoreForm({ nome: "", cognome: "", email: "", telefono: "", codice_agente: "", offerte_abilitate: [], provvigione_percentuale: "2", stato: "attivo" });
                            // Refresh venditori list
                            const resp = await fetch(`${API}/admin/venditori`, { credentials: 'include' });
                            if (resp.ok) setVenditori(await resp.json());
                          }
                        } catch (error) {
                          toast.error('Errore salvataggio');
                        }
                      }}>
                        <Save className="w-4 h-4 mr-2" /> Salva
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Codice</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Telefono</TableHead>
                        <TableHead>Offerte</TableHead>
                        <TableHead>Provvigione</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {venditori.length > 0 ? venditori.map(v => (
                        <TableRow key={v.venditore_id}>
                          <TableCell className="font-mono">{v.codice_agente}</TableCell>
                          <TableCell className="font-medium">{v.nome} {v.cognome}</TableCell>
                          <TableCell>{v.email}</TableCell>
                          <TableCell>{v.telefono}</TableCell>
                          <TableCell>
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                              {v.offerte_abilitate?.length || 0} offerte
                            </span>
                          </TableCell>
                          <TableCell>{v.provvigione_percentuale}%</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              v.stato === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {v.stato}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setEditingVenditore(v);
                              setVenditoreForm(v);
                              setVenditoreDialogOpen(true);
                            }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Nessun venditore registrato. Clicca "Nuovo Venditore" per aggiungere.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* SIMULATORE TAB */}
          <TabsContent value="simulatore">
            <BillSimulator />
          </TabsContent>

          {/* OFFERTE TAB - EXPANDED */}
          <TabsContent value="offerte">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="flex flex-row items-center justify-between px-0">
                <CardTitle className="text-2xl font-bold">Gestione Offerte</CardTitle>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild><Button className="rounded-full bg-primary text-white" onClick={() => handleOpenDialog()}><Plus className="w-4 h-4 mr-2" />Nuova Offerta</Button></DialogTrigger>
                  <DialogContent className="max-w-[95vw] h-[95vh] flex flex-col p-0 gap-0 bg-slate-950 text-white border-slate-800">
                    <DialogHeader className="p-6 border-b border-slate-800">
                      <DialogTitle className="text-2xl flex items-center gap-2">
                        {editingOffer ? <Edit className="w-6 h-6 text-blue-400" /> : <Zap className="w-6 h-6 text-yellow-400" />}
                        {editingOffer ? "Modifica Offerta" : "Nuova Offerta 8Energy"}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                      <div className="flex-1 overflow-y-auto p-6">
                        <Tabs defaultValue="classificazione" className="w-full h-full">
                          <TabsList className="w-full justify-start bg-slate-900 border-b border-slate-800 rounded-none h-12 p-0 mb-6">
                            <TabsTrigger value="classificazione" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">1. Classificazione</TabsTrigger>
                            <TabsTrigger value="identificativi" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">2. Identificativi</TabsTrigger>
                            <TabsTrigger value="pricing" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">3. Pricing</TabsTrigger>
                            <TabsTrigger value="sconti" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">4. Sconti</TabsTrigger>
                            <TabsTrigger value="tecnici" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">5. Tecnici</TabsTrigger>
                            <TabsTrigger value="stato" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">6. Stato</TabsTrigger>
                            <TabsTrigger value="integrazioni" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white h-full px-6 rounded-none border-b-2 border-transparent data-[state=active]:border-primary">7. Integrazioni</TabsTrigger>
                          </TabsList>

                          {/* 1. CLASSIFICAZIONE */}
                          <TabsContent value="classificazione" className="space-y-6 mt-0">
                            <div className="grid grid-cols-3 gap-6">
                              <div className="space-y-2">
                                <Label className="text-slate-400">Tipo Offerta</Label>
                                <Select value={formData.tipo} onValueChange={(v) => {
                                  const updates = { tipo: v };
                                  if (v === "luce") { updates.indice = "PUN"; updates.opzione_bioraria = false; }
                                  else if (v === "gas") { updates.indice = "PSV"; updates.opzione_bioraria = false; }
                                  else if (v === "dual") { updates.indice = "PUN"; updates.indice_gas = "PSV"; }
                                  setFormData({ ...formData, ...updates });
                                }}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="tipo-offerta-select"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="luce">Luce</SelectItem>
                                    <SelectItem value="gas">Gas</SelectItem>
                                    <SelectItem value="dual">Luce + Gas (Dual Fuel)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400">Macro Categoria</Label>
                                <Select value={formData.macro_categoria} onValueChange={(v) => setFormData({ ...formData, macro_categoria: v })}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="domestico">Domestico</SelectItem>
                                    <SelectItem value="altri_usi">Altri Usi (Business)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400">Sotto Categoria</Label>
                                <Select value={formData.sotto_categoria} onValueChange={(v) => setFormData({ ...formData, sotto_categoria: v })}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="residente">Residente</SelectItem>
                                    <SelectItem value="non_residente">Non Residente</SelectItem>
                                    <SelectItem value="condominio">Condominio</SelectItem>
                                    <SelectItem value="pubblica">Pubblica Illuminazione</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-6">
                              <div className="space-y-2">
                                <Label className="text-slate-400">Tipologia Offerta</Label>
                                <Select value={formData.tipologia_offerta} onValueChange={(v) => setFormData({ ...formData, tipologia_offerta: v })}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="fisso">Prezzo Fisso</SelectItem>
                                    <SelectItem value="variabile">Prezzo Variabile</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400">Indice Riferimento {formData.tipo === "dual" ? "(Luce)" : ""}</Label>
                                <Select value={formData.indice} onValueChange={(v) => setFormData({ ...formData, indice: v })}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {(formData.tipo === "luce" || formData.tipo === "dual") && <SelectItem value="PUN">PUN (Luce)</SelectItem>}
                                    {formData.tipo === "gas" && <SelectItem value="PSV">PSV (Gas)</SelectItem>}
                                    <SelectItem value="FISSO">Nessuno (Fisso)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {formData.tipo === "dual" && (
                                <div className="space-y-2">
                                  <Label className="text-slate-400">Indice Riferimento (Gas)</Label>
                                  <Select value={formData.indice_gas || "PSV"} onValueChange={(v) => setFormData({ ...formData, indice_gas: v })}>
                                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="PSV">PSV (Gas)</SelectItem>
                                      <SelectItem value="FISSO">Nessuno (Fisso)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              {(formData.tipo === "luce" || formData.tipo === "dual") && (
                                <div className="flex items-end pb-2">
                                  <div className="flex items-center space-x-2">
                                    <Checkbox 
                                      id="opzione_bioraria" 
                                      checked={formData.opzione_bioraria} 
                                      onCheckedChange={(c) => setFormData({ ...formData, opzione_bioraria: c })}
                                      className="border-slate-600 data-[state=checked]:bg-primary"
                                    />
                                    <Label htmlFor="opzione_bioraria" className="text-white cursor-pointer">Abilita Opzione Bioraria</Label>
                                  </div>
                                </div>
                              )}
                            </div>
                          </TabsContent>

                          {/* 2. IDENTIFICATIVI */}
                          <TabsContent value="identificativi" className="space-y-6 mt-0">
                            <div className="grid grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <Label className="text-slate-400">Nome Offerta (Pubblico)</Label>
                                <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400 flex items-center gap-2">Codice Offerta (135/22 ARERA) <HelpCircle className="w-4 h-4 text-slate-500" title="Codice univoco per il portale offerte" /></Label>
                                <div className="flex gap-2">
                                  <Input value={formData.codice_offerta} readOnly className="bg-slate-900 border-slate-700 text-white font-mono" />
                                  <Button type="button" variant="secondary" onClick={() => copyToClipboard(formData.codice_offerta)}><Copy className="w-4 h-4" /></Button>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-800">
                              <h4 className="text-sm font-semibold text-slate-300 mb-4">Generatore Codice ARERA</h4>
                              <div className="grid grid-cols-4 gap-4">
                                <div><Label className="text-xs text-slate-500">Operatore</Label><Input value={codiceBuilder.codice_arera} readOnly className="h-8 bg-slate-950 border-slate-800 text-xs font-mono" /></div>
                                <div><Label className="text-xs text-slate-500">Tipo</Label><Select value={codiceBuilder.tipologia_offerta} onValueChange={(v) => setCodiceBuilder({...codiceBuilder, tipologia_offerta: v})}><SelectTrigger className="h-8 bg-slate-950 border-slate-800 text-xs"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="S">Simulabile</SelectItem></SelectContent></Select></div>
                                <div><Label className="text-xs text-slate-500">Prezzo</Label><Select value={codiceBuilder.tipologia_prezzo} onValueChange={(v) => setCodiceBuilder({...codiceBuilder, tipologia_prezzo: v})}><SelectTrigger className="h-8 bg-slate-950 border-slate-800 text-xs"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="F">Fisso</SelectItem><SelectItem value="V">Variabile</SelectItem></SelectContent></Select></div>
                                <div><Label className="text-xs text-slate-500">Codice Interno (Univoco)</Label><Input value={codiceBuilder.info_aggiuntive} onChange={(e) => setCodiceBuilder({...codiceBuilder, info_aggiuntive: e.target.value.toUpperCase()})} className="h-8 bg-slate-950 border-slate-800 text-xs font-mono" placeholder="ES. PROMO2026" /></div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-6">
                              <div className="space-y-2">
                                <Label className="text-slate-400">Data Inizio Vendibilità</Label>
                                <Input type="date" value={formData.data_inizio_vendibilita} onChange={(e) => setFormData({ ...formData, data_inizio_vendibilita: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400">Data Fine Vendibilità</Label>
                                <Input type="date" value={formData.data_fine_vendibilita} onChange={(e) => setFormData({ ...formData, data_fine_vendibilita: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-slate-400">Durata (Mesi)</Label>
                                <Input type="number" value={formData.durata_mesi} onChange={(e) => setFormData({ ...formData, durata_mesi: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                              </div>
                            </div>
                          </TabsContent>

                          {/* 3. PRICING */}
                          <TabsContent value="pricing" className="space-y-6 mt-0">
                            <div className="flex items-center space-x-2 mb-4 bg-slate-900 p-3 rounded-lg border border-slate-800">
                              <Checkbox 
                                id="vendita_senza_spread" 
                                checked={formData.vendita_senza_spread} 
                                onCheckedChange={(c) => setFormData({ ...formData, vendita_senza_spread: c })}
                                className="border-slate-600 data-[state=checked]:bg-green-500"
                              />
                              <Label htmlFor="vendita_senza_spread" className="text-white cursor-pointer font-bold">Vendita SENZA Spread (Prezzo all'ingrosso puro)</Label>
                            </div>

                            {/* === SEZIONE LUCE === */}
                            {(formData.tipo === "luce" || formData.tipo === "dual") && (
                              <div className={`space-y-4 ${formData.tipo === "dual" ? "bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4" : ""}`} data-testid="pricing-luce-section">
                                {formData.tipo === "dual" && (
                                  <div className="flex items-center gap-2 mb-2">
                                    <Zap className="w-4 h-4 text-yellow-400" />
                                    <span className="text-sm font-bold text-yellow-300 uppercase tracking-wider">Pricing Luce</span>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <Label className="text-slate-400 text-lg text-primary">Prezzo Principale €/kWh</Label>
                                    <Input 
                                      type="number" step="0.000001"
                                      value={formData.prezzo_kwh} 
                                      onChange={(e) => setFormData({ ...formData, prezzo_kwh: e.target.value })} 
                                      className="bg-slate-900 border-primary/50 text-white text-xl h-14" 
                                      placeholder="0.000000"
                                      data-testid="prezzo-kwh-input"
                                    />
                                    <p className="text-xs text-slate-500">Se variabile, inserire valore spread</p>
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Costo Fisso Comm. (€/mese) {formData.tipo === "dual" ? "- Luce" : ""}</Label>
                                    <Input 
                                      type="number" step="0.01"
                                      value={formData.costo_fisso_mensile} 
                                      onChange={(e) => setFormData({ ...formData, costo_fisso_mensile: e.target.value })} 
                                      className="bg-slate-900 border-slate-700 text-white h-14 text-lg" 
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-6 pt-4 border-t border-slate-800">
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Prezzo F1 (Picco)</Label>
                                    <Input type="number" step="0.000001" value={formData.prezzo_f1} onChange={(e) => setFormData({ ...formData, prezzo_f1: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="prezzo-f1-input" />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Prezzo F2 (Intermedio)</Label>
                                    <Input type="number" step="0.000001" value={formData.prezzo_f2} onChange={(e) => setFormData({ ...formData, prezzo_f2: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="prezzo-f2-input" />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Prezzo F3 (Fuori Picco)</Label>
                                    <Input type="number" step="0.000001" value={formData.prezzo_f3} onChange={(e) => setFormData({ ...formData, prezzo_f3: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="prezzo-f3-input" />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* === SEZIONE GAS === */}
                            {(formData.tipo === "gas" || formData.tipo === "dual") && (
                              <div className={`space-y-4 ${formData.tipo === "dual" ? "bg-orange-500/5 border border-orange-500/20 rounded-xl p-4" : ""}`} data-testid="pricing-gas-section">
                                {formData.tipo === "dual" && (
                                  <div className="flex items-center gap-2 mb-2">
                                    <Flame className="w-4 h-4 text-orange-400" />
                                    <span className="text-sm font-bold text-orange-300 uppercase tracking-wider">Pricing Gas</span>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <Label className="text-slate-400 text-lg text-primary">Prezzo Principale €/Smc</Label>
                                    <Input 
                                      type="number" step="0.000001"
                                      value={formData.tipo === "dual" ? (formData.prezzo_smc_dual || formData.prezzo_smc) : formData.prezzo_smc} 
                                      onChange={(e) => setFormData({ ...formData, [formData.tipo === "dual" ? "prezzo_smc_dual" : "prezzo_smc"]: e.target.value })} 
                                      className="bg-slate-900 border-primary/50 text-white text-xl h-14" 
                                      placeholder="0.000000"
                                      data-testid="prezzo-smc-input"
                                    />
                                    <p className="text-xs text-slate-500">Se variabile, inserire valore spread</p>
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Costo Fisso Comm. (€/mese) {formData.tipo === "dual" ? "- Gas" : ""}</Label>
                                    <Input 
                                      type="number" step="0.01"
                                      value={formData.tipo === "dual" ? (formData.costo_fisso_mensile_gas || "") : formData.costo_fisso_mensile} 
                                      onChange={(e) => setFormData({ ...formData, [formData.tipo === "dual" ? "costo_fisso_mensile_gas" : "costo_fisso_mensile"]: e.target.value })} 
                                      className="bg-slate-900 border-slate-700 text-white h-14 text-lg" 
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* === SCONTO COMBINATO DUAL === */}
                            {formData.tipo === "dual" && (
                              <div className="bg-gradient-to-r from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-xl p-4 space-y-3" data-testid="dual-discount-section">
                                <div className="flex items-center gap-2 mb-1">
                                  <Zap className="w-4 h-4 text-cyan-400" />
                                  <span className="text-sm font-bold text-cyan-300 uppercase tracking-wider">Sconto Combinato Dual Fuel</span>
                                </div>
                                <p className="text-xs text-slate-500">Sconto applicato automaticamente per l'attivazione di Luce + Gas nella stessa offerta</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Valore Sconto Combinato</Label>
                                    <Input 
                                      type="number" step="0.01"
                                      value={formData.sconto_dual_combinato} 
                                      onChange={(e) => setFormData({ ...formData, sconto_dual_combinato: e.target.value })} 
                                      className="bg-slate-900 border-slate-700 text-white" 
                                      placeholder="0.00"
                                      data-testid="sconto-dual-combinato-input"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-slate-400">Tipo Sconto</Label>
                                    <Select value={formData.sconto_dual_combinato_tipo} onValueChange={(v) => setFormData({ ...formData, sconto_dual_combinato_tipo: v })}>
                                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="euro_mese">€/mese</SelectItem>
                                        <SelectItem value="euro_anno">€/anno</SelectItem>
                                        <SelectItem value="percentuale">%</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </div>
                            )}
                          </TabsContent>

                          {/* 4. SCONTI */}
                          <TabsContent value="sconti" className="space-y-6 mt-0">
                            {/* Info Banner */}
                            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/50 rounded-xl p-4">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center shrink-0">
                                  <Euro className="w-5 h-5 text-green-400" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-green-300">Configura gli Sconti dell'Offerta</h4>
                                  <p className="text-sm text-slate-400 mt-1">Attiva gli sconti e imposta i valori. Gli sconti vengono applicati automaticamente ai clienti che rispettano le condizioni.</p>
                                </div>
                              </div>
                            </div>

                            {/* Main Discounts Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {/* Sconto SEPA */}
                              <Card className={`bg-slate-900 border-2 transition-all ${formData.sconto_sepa ? 'border-green-500/50 shadow-lg shadow-green-500/10' : 'border-slate-800'}`}>
                                <CardContent className="p-5">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.sconto_sepa ? 'bg-green-500/20' : 'bg-slate-800'}`}>
                                        <Receipt className={`w-5 h-5 ${formData.sconto_sepa ? 'text-green-400' : 'text-slate-500'}`} />
                                      </div>
                                      <div>
                                        <Label className="text-white font-semibold">Sconto Domiciliazione SEPA</Label>
                                        <p className="text-xs text-slate-500">Addebito automatico su c/c</p>
                                      </div>
                                    </div>
                                    <Checkbox 
                                      checked={formData.sconto_sepa} 
                                      onCheckedChange={(c) => setFormData({ ...formData, sconto_sepa: c })} 
                                      className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" 
                                    />
                                  </div>
                                  {formData.sconto_sepa && (
                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800 animate-in fade-in duration-200">
                                      <div>
                                        <Label className="text-xs text-slate-500">Valore Sconto</Label>
                                        <Input 
                                          type="number" 
                                          step="0.01" 
                                          value={formData.sconto_sepa_valore} 
                                          onChange={(e) => setFormData({ ...formData, sconto_sepa_valore: e.target.value })} 
                                          className="bg-slate-950 border-slate-700 text-white mt-1" 
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Tipo</Label>
                                        <Select value={formData.sconto_sepa_tipo} onValueChange={(v) => setFormData({ ...formData, sconto_sepa_tipo: v })}>
                                          <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="euro_mese">€/mese</SelectItem>
                                            <SelectItem value="euro_anno">€/anno</SelectItem>
                                            <SelectItem value="euro_kwh">€/kWh</SelectItem>
                                            <SelectItem value="euro_smc">€/Smc</SelectItem>
                                            <SelectItem value="percentuale">%</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* Sconto Bolletta Web */}
                              <Card className={`bg-slate-900 border-2 transition-all ${formData.sconto_online ? 'border-blue-500/50 shadow-lg shadow-blue-500/10' : 'border-slate-800'}`}>
                                <CardContent className="p-5">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.sconto_online ? 'bg-blue-500/20' : 'bg-slate-800'}`}>
                                        <FileText className={`w-5 h-5 ${formData.sconto_online ? 'text-blue-400' : 'text-slate-500'}`} />
                                      </div>
                                      <div>
                                        <Label className="text-white font-semibold">Sconto Bolletta Web</Label>
                                        <p className="text-xs text-slate-500">Ricezione digitale fatture</p>
                                      </div>
                                    </div>
                                    <Checkbox 
                                      checked={formData.sconto_online} 
                                      onCheckedChange={(c) => setFormData({ ...formData, sconto_online: c })} 
                                      className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                                    />
                                  </div>
                                  {formData.sconto_online && (
                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800 animate-in fade-in duration-200">
                                      <div>
                                        <Label className="text-xs text-slate-500">Valore Sconto</Label>
                                        <Input 
                                          type="number" 
                                          step="0.01" 
                                          value={formData.sconto_online_valore} 
                                          onChange={(e) => setFormData({ ...formData, sconto_online_valore: e.target.value })} 
                                          className="bg-slate-950 border-slate-700 text-white mt-1" 
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Tipo</Label>
                                        <Select value={formData.sconto_online_tipo} onValueChange={(v) => setFormData({ ...formData, sconto_online_tipo: v })}>
                                          <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="euro_mese">€/mese</SelectItem>
                                            <SelectItem value="euro_anno">€/anno</SelectItem>
                                            <SelectItem value="euro_kwh">€/kWh</SelectItem>
                                            <SelectItem value="euro_smc">€/Smc</SelectItem>
                                            <SelectItem value="percentuale">%</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* Sconto Autolettura */}
                              <Card className={`bg-slate-900 border-2 transition-all ${formData.sconto_auto_bolletta ? 'border-purple-500/50 shadow-lg shadow-purple-500/10' : 'border-slate-800'}`}>
                                <CardContent className="p-5">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.sconto_auto_bolletta ? 'bg-purple-500/20' : 'bg-slate-800'}`}>
                                        <Gauge className={`w-5 h-5 ${formData.sconto_auto_bolletta ? 'text-purple-400' : 'text-slate-500'}`} />
                                      </div>
                                      <div>
                                        <Label className="text-white font-semibold">Sconto Autolettura</Label>
                                        <p className="text-xs text-slate-500">Comunicazione letture contatore</p>
                                      </div>
                                    </div>
                                    <Checkbox 
                                      checked={formData.sconto_auto_bolletta} 
                                      onCheckedChange={(c) => setFormData({ ...formData, sconto_auto_bolletta: c })} 
                                      className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500" 
                                    />
                                  </div>
                                  {formData.sconto_auto_bolletta && (
                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800 animate-in fade-in duration-200">
                                      <div>
                                        <Label className="text-xs text-slate-500">Valore Sconto</Label>
                                        <Input 
                                          type="number" 
                                          step="0.01" 
                                          value={formData.sconto_auto_valore} 
                                          onChange={(e) => setFormData({ ...formData, sconto_auto_valore: e.target.value })} 
                                          className="bg-slate-950 border-slate-700 text-white mt-1" 
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Tipo</Label>
                                        <Select value={formData.sconto_auto_tipo} onValueChange={(v) => setFormData({ ...formData, sconto_auto_tipo: v })}>
                                          <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="euro_mese">€/mese</SelectItem>
                                            <SelectItem value="euro_anno">€/anno</SelectItem>
                                            <SelectItem value="euro_kwh">€/kWh</SelectItem>
                                            <SelectItem value="euro_smc">€/Smc</SelectItem>
                                            <SelectItem value="percentuale">%</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* Sconto Dual Fuel - only show for single tipo offers */}
                              {formData.tipo !== "dual" && (
                              <Card className={`bg-slate-900 border-2 transition-all ${formData.sconto_dual ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-slate-800'}`}>
                                <CardContent className="p-5">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.sconto_dual ? 'bg-orange-500/20' : 'bg-slate-800'}`}>
                                        <Zap className={`w-5 h-5 ${formData.sconto_dual ? 'text-orange-400' : 'text-slate-500'}`} />
                                      </div>
                                      <div>
                                        <Label className="text-white font-semibold">Sconto Dual Fuel</Label>
                                        <p className="text-xs text-slate-500">Luce + Gas insieme</p>
                                      </div>
                                    </div>
                                    <Checkbox 
                                      checked={formData.sconto_dual} 
                                      onCheckedChange={(c) => setFormData({ ...formData, sconto_dual: c })} 
                                      className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500" 
                                    />
                                  </div>
                                  {formData.sconto_dual && (
                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800 animate-in fade-in duration-200">
                                      <div>
                                        <Label className="text-xs text-slate-500">Valore Sconto</Label>
                                        <Input 
                                          type="number" 
                                          step="0.01" 
                                          value={formData.sconto_dual_valore} 
                                          onChange={(e) => setFormData({ ...formData, sconto_dual_valore: e.target.value })} 
                                          className="bg-slate-950 border-slate-700 text-white mt-1" 
                                          placeholder="0.00"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-500">Tipo</Label>
                                        <Select value={formData.sconto_dual_tipo} onValueChange={(v) => setFormData({ ...formData, sconto_dual_tipo: v })}>
                                          <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="euro_mese">€/mese</SelectItem>
                                            <SelectItem value="euro_anno">€/anno</SelectItem>
                                            <SelectItem value="euro_kwh">€/kWh</SelectItem>
                                            <SelectItem value="euro_smc">€/Smc</SelectItem>
                                            <SelectItem value="percentuale">%</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                              )}

                            {/* Bonus Benvenuto - Standalone */}
                            </div>
                            <Card className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border border-yellow-600/30">
                              <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-4">
                                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                                    <span className="text-xl">🎁</span>
                                  </div>
                                  <div>
                                    <Label className="text-yellow-300 font-semibold">Bonus Benvenuto</Label>
                                    <p className="text-xs text-slate-400">Sconto una tantum per nuovi clienti</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-xs text-slate-500">Importo Bonus</Label>
                                    <Input 
                                      type="number" 
                                      step="0.01" 
                                      value={formData.bonus_benvenuto} 
                                      onChange={(e) => setFormData({ ...formData, bonus_benvenuto: e.target.value })} 
                                      className="bg-slate-950 border-slate-700 text-white mt-1" 
                                      placeholder="Es. 50"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">Tipo Bonus</Label>
                                    <Select value={formData.bonus_benvenuto_tipo} onValueChange={(v) => setFormData({ ...formData, bonus_benvenuto_tipo: v })}>
                                      <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="euro">€ (una tantum)</SelectItem>
                                        <SelectItem value="euro_bolletta">€ in bolletta</SelectItem>
                                        <SelectItem value="mesi_gratis">Mesi gratis</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Note aggiuntive */}
                            <div className="space-y-2">
                              <Label className="text-slate-400">Note e Condizioni Sconti</Label>
                              <Textarea 
                                value={formData.note_sconti} 
                                onChange={(e) => setFormData({ ...formData, note_sconti: e.target.value })} 
                                className="bg-slate-900 border-slate-700 text-white min-h-[100px]" 
                                placeholder="Es. Lo sconto SEPA viene applicato dalla seconda bolletta. Il bonus benvenuto è cumulabile con gli altri sconti..."
                              />
                            </div>

                            {/* Summary Preview */}
                            {(formData.sconto_sepa || formData.sconto_online || formData.sconto_auto_bolletta || formData.sconto_dual || formData.bonus_benvenuto) && (
                              <Card className="bg-slate-800/50 border-slate-700">
                                <CardContent className="p-4">
                                  <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" /> Riepilogo Sconti Attivi
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {formData.sconto_sepa && formData.sconto_sepa_valore && (
                                      <span className="bg-green-900/50 text-green-300 px-3 py-1 rounded-full text-sm">
                                        SEPA: {formData.sconto_sepa_valore} {formData.sconto_sepa_tipo === 'percentuale' ? '%' : formData.sconto_sepa_tipo.replace('euro_', '€/')}
                                      </span>
                                    )}
                                    {formData.sconto_online && formData.sconto_online_valore && (
                                      <span className="bg-blue-900/50 text-blue-300 px-3 py-1 rounded-full text-sm">
                                        Web: {formData.sconto_online_valore} {formData.sconto_online_tipo === 'percentuale' ? '%' : formData.sconto_online_tipo.replace('euro_', '€/')}
                                      </span>
                                    )}
                                    {formData.sconto_auto_bolletta && formData.sconto_auto_valore && (
                                      <span className="bg-purple-900/50 text-purple-300 px-3 py-1 rounded-full text-sm">
                                        Autolettura: {formData.sconto_auto_valore} {formData.sconto_auto_tipo === 'percentuale' ? '%' : formData.sconto_auto_tipo.replace('euro_', '€/')}
                                      </span>
                                    )}
                                    {formData.sconto_dual && formData.sconto_dual_valore && (
                                      <span className="bg-orange-900/50 text-orange-300 px-3 py-1 rounded-full text-sm">
                                        Dual: {formData.sconto_dual_valore} {formData.sconto_dual_tipo === 'percentuale' ? '%' : formData.sconto_dual_tipo.replace('euro_', '€/')}
                                      </span>
                                    )}
                                    {formData.bonus_benvenuto && (
                                      <span className="bg-yellow-900/50 text-yellow-300 px-3 py-1 rounded-full text-sm">
                                        Bonus: {formData.bonus_benvenuto}{formData.bonus_benvenuto_tipo === 'mesi_gratis' ? ' mesi' : '€'}
                                      </span>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                          </TabsContent>

                          {/* 5. TECNICI */}
                          <TabsContent value="tecnici" className="space-y-6 mt-0">
                            {(formData.tipo === "luce" || formData.tipo === "dual") && (
                              <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                  <Label className="text-slate-400">Potenza Minima (kW)</Label>
                                  <Input type="number" value={formData.potenza_min} onChange={(e) => setFormData({ ...formData, potenza_min: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="potenza-min-input" />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-slate-400">Potenza Massima (kW)</Label>
                                  <Input type="number" value={formData.potenza_max} onChange={(e) => setFormData({ ...formData, potenza_max: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="potenza-max-input" />
                                </div>
                              </div>
                            )}
                            {formData.tipo === "gas" && (
                              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                                <p className="text-sm text-slate-400">I parametri di potenza non sono applicabili alle offerte Gas.</p>
                              </div>
                            )}
                            {(formData.tipo === "luce" || formData.tipo === "dual") && (
                              <div className="space-y-2">
                                <Label className="text-slate-400">Tipologia Dispacciamento</Label>
                                <Select value={formData.tipologia_dispacciamento} onValueChange={(v) => setFormData({ ...formData, tipologia_dispacciamento: v })}>
                                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="base">Base</SelectItem>
                                    <SelectItem value="reseller">Reseller</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div className="flex items-center space-x-2 pt-4">
                              <Checkbox 
                                id="deposito" 
                                checked={formData.deposito_cauzionale} 
                                onCheckedChange={(c) => setFormData({ ...formData, deposito_cauzionale: c })}
                                className="border-slate-600"
                              />
                              <Label htmlFor="deposito" className="text-white cursor-pointer">Richiedi Deposito Cauzionale (se no SDD)</Label>
                            </div>
                          </TabsContent>

                          {/* 6. STATO & VISIBILITÀ */}
                          <TabsContent value="stato" className="space-y-6 mt-0">
                            <div className="space-y-4">
                              <div className="bg-slate-900 p-4 rounded-lg border border-green-500/20 flex items-center justify-between" data-testid="visibile-online-toggle">
                                <div>
                                  <Label className="text-white font-bold">Visibile Online (Simulazione Utenti)</Label>
                                  <p className="text-xs text-slate-400 mt-0.5">L'offerta appare nella simulazione pubblica per tutti gli utenti</p>
                                </div>
                                <Checkbox checked={formData.visibile_online} onCheckedChange={(c) => setFormData({ ...formData, visibile_online: c })} className="data-[state=checked]:bg-green-500" />
                              </div>
                              <div className="bg-slate-900 p-4 rounded-lg border border-cyan-500/20 flex items-center justify-between" data-testid="solo-admin-toggle">
                                <div>
                                  <Label className="text-white font-bold">Solo Admin (Simulazione Admin)</Label>
                                  <p className="text-xs text-slate-400 mt-0.5">L'offerta appare solo nella simulazione fatta dall'admin, non visibile al pubblico</p>
                                </div>
                                <Checkbox checked={formData.solo_admin} onCheckedChange={(c) => setFormData({ ...formData, solo_admin: c, visibile_online: c ? false : formData.visibile_online })} className="data-[state=checked]:bg-cyan-500" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-slate-900 p-4 rounded-lg border border-slate-800 flex items-center justify-between">
                                <Label className="text-white">Vendibile Online</Label>
                                <Checkbox checked={formData.vendibile_online} onCheckedChange={(c) => setFormData({ ...formData, vendibile_online: c })} className="data-[state=checked]:bg-green-500" />
                              </div>
                              <div className="bg-slate-900 p-4 rounded-lg border border-slate-800 flex items-center justify-between">
                                <Label className="text-white">Offerta PLACET</Label>
                                <Checkbox checked={formData.off_placet} onCheckedChange={(c) => setFormData({ ...formData, off_placet: c })} className="data-[state=checked]:bg-blue-500" />
                              </div>
                              <div className="bg-slate-900 p-4 rounded-lg border border-slate-800 flex items-center justify-between">
                                <Label className="text-white">Per Vulnerabili (Del. 51/18)</Label>
                                <Checkbox checked={formData.off_vulnerabili} onCheckedChange={(c) => setFormData({ ...formData, off_vulnerabili: c })} className="data-[state=checked]:bg-blue-500" />
                              </div>
                            </div>
                          </TabsContent>

                          {/* 7. DOCUMENTI */}
                          <TabsContent value="integrazioni" className="space-y-6 mt-0">
                            <Card className="bg-slate-900 border-slate-800">
                              <CardContent className="p-6 space-y-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-blue-900/20 rounded-full flex items-center justify-center">
                                    <UploadCloud className="w-6 h-6 text-blue-400" />
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-white">Documenti Offerta</h4>
                                    <p className="text-sm text-slate-400">Collegamento ai documenti ufficiali dell'offerta</p>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-slate-400">Link Scheda CTE (PDF)</Label>
                                  <Input value={formData.link_cte} onChange={(e) => setFormData({ ...formData, link_cte: e.target.value })} className="bg-slate-950 border-slate-700 text-white" placeholder="https://..." />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-slate-400">Descrizione Sintetica per Portale</Label>
                                  <Textarea value={formData.descrizione_cte} onChange={(e) => setFormData({ ...formData, descrizione_cte: e.target.value })} className="bg-slate-950 border-slate-700 text-white" />
                                </div>
                              </CardContent>
                            </Card>
                          </TabsContent>
                        </Tabs>
                      </div>

                      {/* Floating Save Bar */}
                      <div className="p-4 border-t border-slate-800 bg-slate-900 flex justify-between items-center">
                        <span className="text-slate-400 text-sm">
                          {formData.nome ? `Modificando: ${formData.nome}` : "Nuova Offerta"}
                        </span>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-white hover:bg-slate-800">Annulla</Button>
                          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 text-white min-w-[150px]">
                            {saving ? "Salvataggio..." : <><Save className="w-4 h-4 mr-2" /> Salva Offerta</>}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Nome</TableHead><TableHead>Codice</TableHead><TableHead>Prezzo</TableHead><TableHead>Visibilità</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {offers.map((o) => (
                        <TableRow key={o.offer_id}>
                          <TableCell><div className={`w-8 h-8 rounded-lg flex items-center justify-center ${o.tipo === "luce" ? "bg-yellow-100" : o.tipo === "dual" ? "bg-cyan-100" : "bg-orange-100"}`}>{o.tipo === "luce" ? <Zap className="w-4 h-4 text-yellow-600" /> : o.tipo === "dual" ? <><Zap className="w-3 h-3 text-yellow-600" /><Flame className="w-3 h-3 text-orange-500" /></> : <Flame className="w-4 h-4 text-orange-500" />}</div></TableCell>
                          <TableCell className="font-medium">{o.nome}</TableCell>
                          <TableCell>{o.codice_offerta ? <code className="text-xs bg-gray-100 px-2 py-1 rounded">{o.codice_offerta.slice(0, 15)}...</code> : "-"}</TableCell>
                          <TableCell>{o.tipo === "dual" ? <span className="text-xs">€{o.prezzo_kwh?.toFixed(3)}/kWh + €{(o.prezzo_smc || o.prezzo_smc_dual)?.toFixed(3)}/Smc</span> : o.tipo === "luce" ? `€${o.prezzo_kwh?.toFixed(3)}/kWh` : `€${o.prezzo_smc?.toFixed(3)}/Smc`}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {o.visibile_online !== false && !o.solo_admin && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full" data-testid="badge-online">Online</span>}
                              {o.solo_admin && <span className="text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full" data-testid="badge-admin">Solo Admin</span>}
                              {o.visibile_online === false && !o.solo_admin && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Nascosta</span>}
                            </div>
                          </TableCell>
                          <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleOpenDialog(o)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteOffer(o.offer_id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" />Gestione Utenti & Amministratori</CardTitle>
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-primary text-white">
                      <UserPlus className="w-4 h-4 mr-2" />Nuovo Utente
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Crea Nuovo Utente</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div><Label>Nome</Label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required /></div>
                      <div><Label>Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required /></div>
                      <div><Label>Password</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required /></div>
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="is_admin" 
                          checked={userForm.is_admin} 
                          onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })} 
                          className="w-4 h-4"
                        />
                        <Label htmlFor="is_admin">Assegna privilegi di Amministratore</Label>
                      </div>
                      <DialogFooter>
                        <Button type="submit" disabled={saving}>{saving ? "..." : "Crea Utente"}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Data Registrazione</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          {u.is_admin ? (
                            <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                              <Shield className="w-3 h-3" /> Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                              User
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => { setResetPwUser(u); setNewPassword(""); }} title="Reset Password" data-testid={`reset-pw-${u.user_id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          {u.is_admin ? (
                            <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => handleUpdateRole(u.user_id, false)} title="Rimuovi privilegi admin">
                              <ShieldOff className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="text-green-600" onClick={() => handleUpdateRole(u.user_id, true)} title="Promuovi a admin">
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteUser(u.user_id)} title="Elimina utente">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Password Reset Dialog */}
                <Dialog open={!!resetPwUser} onOpenChange={(open) => { if (!open) { setResetPwUser(null); setNewPassword(""); } }}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-600" /> Reset Password</DialogTitle>
                    </DialogHeader>
                    {resetPwUser && (
                      <div className="space-y-4">
                        <div className="bg-gray-50 rounded-lg p-3 border">
                          <p className="text-sm font-medium">{resetPwUser.name}</p>
                          <p className="text-xs text-muted-foreground">{resetPwUser.email}</p>
                        </div>
                        <div>
                          <Label>Nuova Password</Label>
                          <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Minimo 6 caratteri"
                            data-testid="new-password-input"
                          />
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => { setResetPwUser(null); setNewPassword(""); }}>Annulla</Button>
                          <Button onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 6} data-testid="confirm-reset-pw">
                            <KeyRound className="w-4 h-4 mr-2" /> Aggiorna Password
                          </Button>
                        </DialogFooter>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACTIVATION REQUESTS TAB */}
          <TabsContent value="activation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Richieste di Attivazione Luce/Gas
                </CardTitle>
                <CardDescription>
                  Gestisci le richieste di nuova attivazione o allaccio
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activationRequests.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">Nessuna richiesta di attivazione ricevuta</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Codice POD/PDR</TableHead>
                          <TableHead>Comune</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activationRequests.map((req) => (
                          <TableRow key={req.request_id}>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(req.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                req.tipo_fornitura === "luce" 
                                  ? "bg-yellow-100 text-yellow-700" 
                                  : "bg-orange-100 text-orange-700"
                              }`}>
                                {req.tipo_fornitura === "luce" ? "⚡ Luce" : "🔥 Gas"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{req.cliente_nome}</div>
                              <div className="text-xs text-muted-foreground">{req.telefono}</div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{req.codice_pod_pdr}</TableCell>
                            <TableCell>{req.fornitura_comune}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                req.status === "pending" ? "bg-amber-100 text-amber-700" :
                                req.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                req.status === "completed" ? "bg-green-100 text-green-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {req.status === "pending" ? "In attesa" :
                                 req.status === "in_progress" ? "In lavorazione" :
                                 req.status === "completed" ? "Completata" : "Rifiutata"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Select
                                  value={req.status}
                                  onValueChange={async (newStatus) => {
                                    try {
                                      const res = await fetch(`${API}/admin/activation-requests/${req.request_id}/status?status=${newStatus}`, {
                                        method: "PUT",
                                        credentials: "include"
                                      });
                                      if (res.ok) {
                                        setActivationRequests(prev => prev.map(r => 
                                          r.request_id === req.request_id ? { ...r, status: newStatus } : r
                                        ));
                                        toast.success("Stato aggiornato");
                                      }
                                    } catch (err) {
                                      toast.error("Errore nell'aggiornamento");
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-[140px] h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">In attesa</SelectItem>
                                    <SelectItem value="in_progress">In lavorazione</SelectItem>
                                    <SelectItem value="completed">Completata</SelectItem>
                                    <SelectItem value="rejected">Rifiutata</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(`${API}/activation-requests/${req.request_id}/pdf`, { credentials: "include" });
                                      if (!response.ok) throw new Error("Errore PDF");
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement("a");
                                      a.href = url;
                                      a.download = `contratto_${req.request_id}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    } catch (err) {
                                      toast.error("Errore download PDF");
                                    }
                                  }}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SWITCH REQUESTS TAB */}
          <TabsContent value="switch">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Richieste di Switch Online</CardTitle>
              </CardHeader>
              <CardContent>
                {switchRequests.length === 0 ? <p className="text-muted-foreground text-center py-8">Nessuna richiesta di switch ricevuta</p> : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Fornitura</TableHead>
                        <TableHead>Offerta</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {switchRequests.map((req) => (
                          <TableRow key={req.request_id}>
                            <TableCell className="text-sm text-muted-foreground">{new Date(req.created_at).toLocaleDateString()} {new Date(req.created_at).toLocaleTimeString()}</TableCell>
                            <TableCell>
                              <div className="font-medium">{req.nome} {req.cognome}</div>
                              <div className="text-xs text-muted-foreground">{req.codice_fiscale}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {req.tipo_fornitura === "luce" ? <Zap className="w-3 h-3 text-yellow-500" /> : <Flame className="w-3 h-3 text-orange-500" />}
                                <span className="font-medium">{req.comune_fornitura}</span>
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">{req.codice_pod_pdr}</div>
                            </TableCell>
                            <TableCell>{req.offer_name || req.offer_id}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                req.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                req.status === 'completed' ? 'bg-green-100 text-green-700' :
                                req.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {req.status === 'new' ? 'Nuova' : 
                                 req.status === 'processing' ? 'In Lavorazione' :
                                 req.status === 'completed' ? 'Completata' : req.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => {
                                // Simple update to completed for MVP demo
                                if (confirm("Segnare come completata?")) {
                                  fetch(`${API}/admin/switch/requests/${req.request_id}/status`, {
                                    method: "PUT",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: "include",
                                    body: JSON.stringify({ status: "completed" })
                                  }).then(() => {
                                    toast.success("Stato aggiornato");
                                    fetchData();
                                  });
                                }
                              }}>
                                Completa
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PRE-CHECK TAB */}
          <TabsContent value="precheck">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><FileCheck className="w-5 h-5" />Verifica POD/PDR</CardTitle></CardHeader>
                <CardContent>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-700"><Info className="w-4 h-4 inline mr-1" />Verifica la corrispondenza tra codice POD/PDR e dati cliente</p>
                  </div>
                  <form onSubmit={handlePrecheck} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Tipo</Label><Select value={precheckForm.tipo} onValueChange={(v) => setPrecheckForm({ ...precheckForm, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pod"><Zap className="w-4 h-4 inline mr-2 text-yellow-500" />POD (Luce)</SelectItem><SelectItem value="pdr"><Flame className="w-4 h-4 inline mr-2 text-orange-500" />PDR (Gas)</SelectItem></SelectContent></Select></div>
                      <div><Label>Codice {precheckForm.tipo.toUpperCase()}</Label><Input value={precheckForm.codice} onChange={(e) => setPrecheckForm({ ...precheckForm, codice: e.target.value.toUpperCase() })} placeholder={precheckForm.tipo === "pod" ? "IT001E12345678" : "12345678901234"} required /></div>
                    </div>
                    <div><Label>Nome Cliente</Label><Input value={precheckForm.nome_cliente} onChange={(e) => setPrecheckForm({ ...precheckForm, nome_cliente: e.target.value })} required /></div>
                    <div><Label>Codice Fiscale</Label><Input value={precheckForm.codice_fiscale} onChange={(e) => setPrecheckForm({ ...precheckForm, codice_fiscale: e.target.value.toUpperCase() })} maxLength={16} required /></div>
                    <div><Label>Indirizzo (opzionale)</Label><Input value={precheckForm.indirizzo} onChange={(e) => setPrecheckForm({ ...precheckForm, indirizzo: e.target.value })} /></div>
                    <Button type="submit" className="w-full" disabled={precheckLoading}>{precheckLoading ? "Verifica..." : <><Search className="w-4 h-4 mr-2" />Verifica</>}</Button>
                  </form>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Storico Verifiche</CardTitle></CardHeader>
                <CardContent>
                  {precheckHistory.length === 0 ? <p className="text-muted-foreground text-center py-8">Nessuna verifica effettuata</p> : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {precheckHistory.map((p) => (
                        <div key={p.precheck_id} className={`p-3 rounded-lg border ${p.esito === "verificato" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                          <div className="flex items-center justify-between mb-1">
                            <code className="font-mono text-sm">{p.codice}</code>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${p.esito === "verificato" ? "bg-green-200 text-green-700" : "bg-red-200 text-red-700"}`}>{p.esito === "verificato" ? "✓ Verificato" : "✗ Errore"}</span>
                          </div>
                          <p className="text-sm">{p.nome_cliente}</p>
                          {p.dettagli && <p className="text-xs text-red-600 mt-1">{p.dettagli}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PLAFOND IVA TAB */}
          <TabsContent value="plafond">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" />Gestione Plafond IVA</CardTitle>
                <Dialog open={plafondDialogOpen} onOpenChange={setPlafondDialogOpen}>
                  <DialogTrigger asChild><Button className="rounded-full bg-primary text-white" onClick={() => handleOpenPlafondDialog()}><Plus className="w-4 h-4 mr-2" />Nuovo Plafond</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{editingPlafond ? "Modifica" : "Nuovo"} Plafond IVA</DialogTitle></DialogHeader>
                    <form onSubmit={handlePlafondSubmit} className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><p className="text-sm text-blue-700"><Info className="w-4 h-4 inline mr-1" />Soglia di imponibile non assoggettato ad IVA riconosciuta dall'Agenzia delle Entrate</p></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>ID Cliente</Label><Input value={plafondForm.cliente_id} onChange={(e) => setPlafondForm({ ...plafondForm, cliente_id: e.target.value })} required /></div>
                        <div><Label>N° Protocollo</Label><Input value={plafondForm.numero_protocollo} onChange={(e) => setPlafondForm({ ...plafondForm, numero_protocollo: e.target.value })} required /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Data Inizio</Label><Input type="date" value={plafondForm.data_inizio} onChange={(e) => setPlafondForm({ ...plafondForm, data_inizio: e.target.value })} required /></div>
                        <div><Label>Data Fine</Label><Input type="date" value={plafondForm.data_fine} onChange={(e) => setPlafondForm({ ...plafondForm, data_fine: e.target.value })} required /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Importo Plafond €</Label><Input type="number" step="0.01" value={plafondForm.importo_plafond} onChange={(e) => setPlafondForm({ ...plafondForm, importo_plafond: e.target.value })} required /></div>
                        <div><Label>Importo Utilizzato €</Label><Input type="number" step="0.01" value={plafondForm.importo_utilizzato} onChange={(e) => setPlafondForm({ ...plafondForm, importo_utilizzato: e.target.value })} /></div>
                      </div>
                      <div><Label>Note</Label><Textarea value={plafondForm.note} onChange={(e) => setPlafondForm({ ...plafondForm, note: e.target.value })} rows={2} /></div>
                      <DialogFooter><Button type="button" variant="outline" onClick={() => setPlafondDialogOpen(false)}>Annulla</Button><Button type="submit" disabled={saving}>{saving ? "..." : <><Save className="w-4 h-4 mr-2" />Salva</>}</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {plafonds.length === 0 ? <p className="text-muted-foreground text-center py-8">Nessun Plafond IVA registrato</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Protocollo</TableHead><TableHead>Validità</TableHead><TableHead>Plafond</TableHead><TableHead>Utilizzato</TableHead><TableHead>Residuo</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {plafonds.map((p) => (
                        <TableRow key={p.plafond_id}>
                          <TableCell className="font-medium">{p.cliente_id}</TableCell>
                          <TableCell><code className="text-xs bg-gray-100 px-2 py-1 rounded">{p.numero_protocollo}</code></TableCell>
                          <TableCell className="text-sm">{p.data_inizio} → {p.data_fine}</TableCell>
                          <TableCell className="font-bold">€{p.importo_plafond?.toLocaleString()}</TableCell>
                          <TableCell className="text-orange-600">€{p.importo_utilizzato?.toLocaleString()}</TableCell>
                          <TableCell className="text-green-600 font-bold">€{p.importo_residuo?.toLocaleString()}</TableCell>
                          <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleOpenPlafondDialog(p)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeletePlafond(p.plafond_id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VERIFICA AZIENDA TAB */}
          <TabsContent value="azienda">
            <CompanyVerification />
          </TabsContent>

          {/* RIFERIMENTO TAB */}
          <TabsContent value="riferimento">
            <div className="space-y-6">
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" />Zone Termiche (Gas)</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{referenceData?.zone_termiche && Object.entries(referenceData.zone_termiche).map(([k, z]) => (<div key={k} className="bg-orange-50 border border-orange-200 rounded-lg p-3"><p className="font-semibold text-orange-700">{z.nome}</p><p className="text-sm text-orange-600">{z.regioni.join(", ")}</p></div>))}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><Gauge className="w-5 h-5" />Cluster Contatore</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">{referenceData?.cluster_contatore && Object.entries(referenceData.cluster_contatore).map(([c, i]) => (<div key={c} className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2"><code className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded text-xs font-bold">{c}</code><span className="text-sm text-yellow-700">{i.nome}</span></div>))}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Fasce Consumo</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-3 gap-4">{referenceData?.fasce_consumo && Object.entries(referenceData.fasce_consumo).map(([k, f]) => (<div key={k} className={`rounded-lg p-4 border ${k === 'F1' ? 'bg-red-50 border-red-200' : k === 'F2' ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}><p className={`font-bold text-lg ${k === 'F1' ? 'text-red-600' : k === 'F2' ? 'text-yellow-600' : 'text-green-600'}`}>{k}</p><p className="font-medium">{f.nome}</p><p className="text-sm text-muted-foreground mt-1">{f.descrizione}</p></div>))}</div></CardContent></Card>
              <div className="grid grid-cols-2 gap-4">
                <Card><CardHeader><CardTitle>Perdite di Rete</CardTitle></CardHeader><CardContent><div className="flex gap-4"><div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center flex-1"><p className="text-3xl font-bold text-blue-600">{(referenceData?.perdite_rete?.BT * 100)}%</p><p className="text-sm text-blue-700">Bassa Tensione</p></div><div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center flex-1"><p className="text-3xl font-bold text-purple-600">{(referenceData?.perdite_rete?.MT * 100)}%</p><p className="text-sm text-purple-700">Media Tensione</p></div></div></CardContent></Card>
                <Card><CardHeader><CardTitle>Oneri di Sistema</CardTitle></CardHeader><CardContent><div className="space-y-2"><p className="font-semibold text-sm"><Zap className="w-4 h-4 inline text-yellow-500" /> Power: ASOS, ARIM</p><p className="font-semibold text-sm"><Flame className="w-4 h-4 inline text-orange-500" /> Gas: RE, UG2, UG3, RS</p></div></CardContent></Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
